'use client';

import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCheck } from 'lucide-react';

import type {
  CareStatePriorAuthRequestT,
  CareStateRequestStatusT,
  CareStateRequestT,
  PriorAuthSummaryT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { useCareStateDomain } from '@/entities/care-state-domain';
import { usePatientPriorAuthsQuery } from '@/entities/prior-auth';
import { useUserInfoQuery } from '@/entities/user-info';
import { AgentTweakableArea } from '@/features/copilot/agent-tweakable';
import { useCopilotComposer } from '@/features/copilot/lib';
import { CortexPromptStrip } from '@/features/cortex-prompts';
import { hasUnfinishedRequestActions } from '@/features/prescription-cancellation';
import { fetchPatientSuggestions } from '@/features/patient-suggestions/api/fetchPatientSuggestions';
import { PatientSuggestionsSection } from '@/features/patient-suggestions/ui/PatientSuggestionsSection';
import { ResolvedSuggestionsSection } from '@/features/patient-suggestions/ui/ResolvedSuggestionsSection';
import { openPriorAuthActionOverlay } from '@/features/prior-auth-form';
import { describePriorAuth, groupPriorAuth, PriorAuthCaseDrawer } from '@/features/prior-auth-tab';
import { PATIENT_SUGGESTIONS_ENABLED } from '@/shared/config/flags';
import { useCopilotOverlays } from '@/shared/stores/ui';
import { useProposedActionsPlan } from '@/features/proposed-actions-plan';
import { Button } from '@/shared/ui/shadcn/button';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';

import { groupActionsByRequest } from '../model/groupActionsByRequest';
import { WRAP_UP_CHART_PROMPT, WRAP_UP_CHART_PROMPT_SOURCE } from '../model/wrapUpChartPrompt';
import { RequestListCard } from './RequestListCard';

type RequestFilter = 'unresolved' | 'resolved';

const UNRESOLVED_STATUSES = new Set<CareStateRequestStatusT>(['Open', 'Pending Follow Up']);

function isUnresolved(request: CareStateRequestT): boolean {
  return UNRESOLVED_STATUSES.has(request.status);
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? `${count} ${singular}` : `${count} ${plural}`;
}

function firstName(fullName: string): string {
  const trimmed = fullName.trim();
  if (trimmed.length === 0) {
    return '';
  }
  const [first = ''] = trimmed.split(/\s+/);
  return first;
}

function formatShortDate(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function priorAuthCaseNumberFromTitle(title: string): string | null {
  return title.match(/\(case (\d+)\)\s*$/i)?.[1] ?? null;
}

function priorAuthContext(summary: PriorAuthSummaryT): string | undefined {
  const group = groupPriorAuth(summary);
  const deadline = formatShortDate(summary.nextResponseDeadline);
  if (group === 'waiting') {
    return [deadline ? `Expected update by ${deadline}` : null, 'No action is needed from you.']
      .filter(Boolean)
      .join(' · ');
  }
  if (group === 'needs-you' && deadline) {
    return `Respond by ${deadline}`;
  }
  return undefined;
}

function fallbackPriorAuthSummary(row: CareStatePriorAuthRequestT): PriorAuthSummaryT {
  return {
    id: row.id,
    dosespotPriorAuthCaseNumber: row.dosespot_prior_auth_case_number ?? null,
    dosespotPrescriptionId: row.dosespot_prescription_id,
    prescriptionRecordId: row.prescription_record_id,
    status: row.status,
    currentStatus: null,
    isExpedited: false,
    attachmentRequired: false,
    isOffline: row.is_offline ?? false,
    canAppeal: null,
    authorizationNumber: row.prior_auth_number ?? null,
    nextResponseDeadline: null,
    currentStatusDate: null,
    lastSeenAt: null,
  };
}

export interface RequestsListTabProps {
  userId: string;
}

export function RequestsListTab({ userId }: RequestsListTabProps) {
  const { careState } = useCareStateDomain();
  const userInfoQuery = useUserInfoQuery(userId);
  const suggestionsQuery = useQuery({
    queryKey: ['/greenfield/care', userId, 'suggestions'],
    queryFn: ({ signal }) => fetchPatientSuggestions(userId, signal),
    enabled: PATIENT_SUGGESTIONS_ENABLED && userId.length > 0,
  });
  const priorAuthsQuery = usePatientPriorAuthsQuery(userId);
  const { actions } = useProposedActionsPlan();
  const { sendText } = useCopilotComposer();
  const overlays = useCopilotOverlays();
  const [filter, setFilter] = useState<RequestFilter>('unresolved');
  const [selectedPriorAuth, setSelectedPriorAuth] = useState<PriorAuthSummaryT | null>(null);

  const patientFirstName = firstName(userInfoQuery.data?.patientInfo?.name ?? '');
  const displayName = patientFirstName.length > 0 ? patientFirstName : 'This patient';

  const { unresolvedRequests, resolvedRequests } = useMemo(() => {
    const unresolved: CareStateRequestT[] = [];
    const resolved: CareStateRequestT[] = [];
    for (const request of careState?.requests ?? []) {
      if (isUnresolved(request)) {
        const isPriorAuth =
          request.kind === 'Prior Auth' ||
          careState?.prior_auth_requests.some(
            (pa) => pa.link.type === 'request' && pa.link.id === request.id
          );
        // Requests is the clinician's action list. Waiting cases remain in Prior Auths.
        if (isPriorAuth && careState && !hasUnfinishedRequestActions(careState, request.id)) {
          continue;
        }
        unresolved.push(request);
      } else {
        resolved.push(request);
      }
    }
    return { unresolvedRequests: unresolved, resolvedRequests: resolved };
  }, [careState]);

  const visibleRequests = filter === 'unresolved' ? unresolvedRequests : resolvedRequests;
  const unresolvedCount = unresolvedRequests.length;

  const actionsByRequest = useMemo(() => groupActionsByRequest(actions), [actions]);
  const priorAuthByRequestId = useMemo(() => {
    const summaries = priorAuthsQuery.data ?? [];
    const summariesById = new Map(summaries.map((summary) => [summary.id, summary] as const));
    const summariesByCaseNumber = new Map(
      summaries.flatMap((summary) =>
        summary.dosespotPriorAuthCaseNumber
          ? [[summary.dosespotPriorAuthCaseNumber, summary] as const]
          : []
      )
    );
    const summariesByRequestId = new Map(
      (careState?.prior_auth_requests ?? []).flatMap((row) => {
        if (row.link.type !== 'request') {
          return [];
        }
        return [[row.link.id, summariesById.get(row.id) ?? fallbackPriorAuthSummary(row)] as const];
      })
    );

    // CareState request and PA-mirror events arrive independently. During
    // that brief race, recover the already-persisted PA summary from the case
    // number in the canonical request title so the card does not go blank.
    for (const request of careState?.requests ?? []) {
      if (summariesByRequestId.has(request.id)) {
        continue;
      }
      const caseNumber = priorAuthCaseNumberFromTitle(request.title);
      if (caseNumber === null) {
        continue;
      }
      const summary = summariesByCaseNumber.get(caseNumber);
      if (summary) {
        summariesByRequestId.set(request.id, summary);
      }
    }

    return summariesByRequestId;
  }, [careState?.prior_auth_requests, careState?.requests, priorAuthsQuery.data]);
  const handleWrapUp = () => {
    sendText({ text: WRAP_UP_CHART_PROMPT, source: WRAP_UP_CHART_PROMPT_SOURCE });
  };

  const handleOpenPriorAuthRequest = useCallback(
    (summary: PriorAuthSummaryT) => {
      openPriorAuthActionOverlay(overlays, {
        recordId: summary.id,
        patientUserId: userId,
        dosespotPrescriptionId: summary.dosespotPrescriptionId,
      });
    },
    [overlays, userId]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="contents">
        <header className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <h2 className="min-w-0 truncate text-lg font-medium leading-tight tracking-tight">
              <span className="text-muted-foreground">{displayName} is presenting with </span>
              <span className="text-foreground">
                {PATIENT_SUGGESTIONS_ENABLED
                  ? suggestionsQuery.isError
                    ? 'Suggestions unavailable'
                    : pluralize(
                        suggestionsQuery.data?.filter(
                          (suggestion) => suggestion.lifecycle.type === 'open'
                        ).length ?? 0,
                        'unresolved suggestion',
                        'unresolved suggestions'
                      )
                  : pluralize(unresolvedCount, 'unresolved request', 'unresolved requests')}
              </span>
            </h2>
            <Tabs value={filter} onValueChange={(next) => setFilter(next as RequestFilter)}>
              <TabsList>
                <TabsTrigger value="unresolved">Unresolved</TabsTrigger>
                <TabsTrigger value="resolved">Resolved</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <Button
            type="button"
            radius="pill"
            size="chip"
            disabled={unresolvedCount === 0}
            onClick={handleWrapUp}
            className="gap-1.5 bg-foreground text-sm text-background hover:bg-foreground/90"
          >
            <CheckCheck className="size-5" aria-hidden="true" />
            <span>Update and close chart</span>
          </Button>
        </header>
        <CortexPromptStrip />
      </div>

      {PATIENT_SUGGESTIONS_ENABLED ? (
        filter === 'unresolved' ? (
          <PatientSuggestionsSection userId={userId} />
        ) : (
          <ResolvedSuggestionsSection userId={userId} />
        )
      ) : (
        <AgentTweakableArea prefix="request">
          <div className="flex flex-col gap-3">
            {visibleRequests.map((request) => {
              const priorAuth = priorAuthByRequestId.get(request.id);
              const priorAuthCopy = priorAuth ? describePriorAuth(priorAuth) : null;
              return (
                <RequestListCard
                  key={request.id}
                  request={request}
                  actions={actionsByRequest.get(request.id) ?? []}
                  onViewPriorAuth={priorAuth ? () => setSelectedPriorAuth(priorAuth) : undefined}
                  priorAuthPresentation={
                    priorAuth && priorAuthCopy
                      ? {
                          label: priorAuthCopy.label,
                          description: priorAuthCopy.description,
                          actionLabel: priorAuthCopy.actionLabel,
                          group: groupPriorAuth(priorAuth),
                          context: priorAuthContext(priorAuth),
                        }
                      : undefined
                  }
                />
              );
            })}
          </div>
        </AgentTweakableArea>
      )}

      <PriorAuthCaseDrawer
        userId={userId}
        summary={selectedPriorAuth}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedPriorAuth(null);
          }
        }}
        onOpenRequest={handleOpenPriorAuthRequest}
      />
    </div>
  );
}
